import {ConfirmationDialog, FormDialog, ReorderingPanel} from '@management-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {ServiceContext} from '../../../../components/Services';
import ProductCertificationDetailForm from '../../forms/ProductCertificationDetailForm';

const CertificateDetails = ({product, onUpdated}) => {
  const services = useContext(ServiceContext);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState({});

  const formatDetails = useCallback((list) => (
    list.map((detail) => ({
      entity: detail,
      attributes: [detail.title, detail.value]
    }))
  ), []);

  useEffect(() => {
    if (product) {
      setDetails(formatDetails(product.certification_details));
    }
  }, [product, formatDetails]);

  const handleSaved = useCallback((updated) => {
    setShowEdit(false);
    onUpdated(updated);
  }, [onUpdated]);

  const handleDelete = useCallback((confirmed) => {
    setShowDelete(false);
    if (confirmed) {
      setLoading(true);
      services.product.deleteCertificationDetail(product, selected).then(updated => {
        setLoading(false);
        onUpdated(updated);
      }).catch(() => setLoading(false));
    }
  }, [services, product, selected, onUpdated]);

  const handleReorder = useCallback((reordered) => {
    setDetails(formatDetails(reordered));
    return services.product.reorderCertificationDetails(product, reordered.map((detail, index) => ({
      id: detail.id,
      index: index + 1
    })));
  }, [services, product, formatDetails]);

  return (
    <>
      <ReorderingPanel
        title="Certificate Details"
        items={details}
        loading={loading}
        onLoading={setLoading}
        controls={[
          {
            title: 'Edit Detail',
            icon: <EditIcon/>,
            onClick: (detail) => setSelected(detail) || setShowEdit(true)
          },
          {
            title: 'Delete Detail',
            icon: <DeleteIcon/>,
            onClick: (detail) => setSelected(detail) || setShowDelete(true)
          }
        ]}
        onNew={() => setSelected(null) || setShowEdit(true)}
        onReorder={handleReorder}
        onReordered={onUpdated}
      />
      <FormDialog
        title={selected ? 'Edit Detail' : 'Add New Detail'}
        open={showEdit}
        maxWidth="md"
        onClose={() => setShowEdit(false)}
        render={useMemo(() => (props) => (
          <ProductCertificationDetailForm {...props} product={product} detail={selected ?? {}} onSaved={handleSaved}/>
        ), [product, handleSaved, selected])}
      />
      <ConfirmationDialog
        title="Delete Detail"
        message="Are you sure you want to delete this detail?"
        open={showDelete}
        onClose={handleDelete}
      />
    </>
  );
}

export default CertificateDetails;
